import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  Users,
  Scale,
  FileText,
  ClipboardList,
  CreditCard,
  Star,
  TrendingUp,
  Banknote,
  Lock,
} from 'lucide-react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import { formatBRL } from '@/lib/utils';

interface AdminStats {
  users: number;
  lawyers: number;
  contracts: number;
  tasks: number;
  payments: number;
  reviews: number;
  tasksByStatus: Record<string, number>;
  gmvCents: number;
  platformRevenueCents: number;
  escrowedCents: number;
  platformFeePercent: number;
  generatedAt: string;
}

export function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => api.get<AdminStats>('/admin/stats'),
    refetchInterval: 60_000,
  });

  return (
    <AdminShell>
      <div className="max-w-7xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-display-lg">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Visão geral da plataforma em tempo real.</p>
          </div>
          {data && (
            <p className="text-xs text-muted-foreground">
              Atualizado {new Date(data.generatedAt).toLocaleTimeString('pt-BR')}
            </p>
          )}
        </div>

        {isLoading && <p className="text-muted-foreground mt-8">Carregando…</p>}

        {data && (
          <>
            {/* Money KPIs */}
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <Kpi
                icon={TrendingUp}
                label="GMV total"
                value={formatBRL(data.gmvCents)}
                hint="Volume bruto transacionado"
                accent
              />
              <Kpi
                icon={Banknote}
                label={`Receita plataforma (${data.platformFeePercent}%)`}
                value={formatBRL(data.platformRevenueCents)}
                hint="Acumulado de fees"
              />
              <Kpi
                icon={Lock}
                label="Em escrow"
                value={formatBRL(data.escrowedCents)}
                hint="Aguardando aprovação do usuário"
              />
            </div>

            {/* Entity counts */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <Kpi icon={Users} label="Usuários" value={String(data.users)} />
              <Kpi icon={Scale} label="Advogados" value={String(data.lawyers)} />
              <Kpi icon={FileText} label="Contratos" value={String(data.contracts)} />
              <Kpi icon={ClipboardList} label="Tarefas" value={String(data.tasks)} />
              <Kpi icon={CreditCard} label="Pagamentos" value={String(data.payments)} />
              <Kpi icon={Star} label="Avaliações" value={String(data.reviews)} />
            </div>

            {/* Tasks by status */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="text-lg">Tarefas por status</CardTitle>
                <CardDescription>Distribuição operacional do funil.</CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(data.tasksByStatus).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma tarefa criada ainda.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.tasksByStatus).map(([status, count]) => (
                      <Badge key={status} variant={statusBadgeVariant(status)}>
                        {statusLabel(status)}: {count}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <Card className={accent ? 'border-accent/40 bg-accent/5' : ''}>
      <CardContent className="pt-6">
        <Icon className={`h-4 w-4 ${accent ? 'text-accent' : 'text-muted-foreground'}`} />
        <p className="text-xs uppercase tracking-wide text-muted-foreground mt-3">{label}</p>
        <p className="font-serif text-2xl mt-1">{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function statusLabel(s: string): string {
  return {
    open: 'Aberta',
    assigned: 'Atribuída',
    in_progress: 'Em andamento',
    delivered: 'Entregue',
    completed: 'Concluída',
    rejected: 'Rejeitada',
    cancelled: 'Cancelada',
    disputed: 'Em disputa',
  }[s] ?? s;
}

function statusBadgeVariant(s: string): 'default' | 'success' | 'warning' | 'danger' | 'muted' {
  if (s === 'completed' || s === 'delivered') return 'success';
  if (s === 'open' || s === 'assigned' || s === 'in_progress') return 'warning';
  if (s === 'disputed') return 'danger';
  if (s === 'cancelled' || s === 'rejected') return 'muted';
  return 'default';
}
